import { computed, ref } from "vue";
import { defineStore, storeToRefs } from "pinia";
import handleError from "@utils/composables/handleError";
import useMyFetch from "@stores/useMyFetch";
import { useMCreativesStore } from "@stores/mCreatives";
import { useMTemplatesStore } from "@stores/mTemplates";
import { useMMediumUploadsStore } from "@stores/mMediumUploads";
import { deepCopy } from "@utils/toolBox";
import { creativeTemplates, creativeUploads, findMMedium } from "@composables/creativeSummary";
import { captureEvent } from "@/lib/analytics";

export const useMMediaStore = defineStore("mMedia", () => {
  const {
    creatives,
    initialCreatives,
    selectedMCreative: mCreative,
    adSetVariables,
  } = storeToRefs(useMCreativesStore());
  const { mTemplates } = storeToRefs(useMTemplatesStore());
  const { mMediumUploads } = storeToRefs(useMMediumUploadsStore());

  const previewMMedium = ref({});
  const previewPlacement = ref("feed");
  const builds = ref([]);

  const previewMMediumUrl = computed(
    () => previewMMedium.value?.url || getPreviewUrl(previewMMedium.value.m_medium_upload_id),
  );

  const previewTemplate = computed(() => mTemplates.value.find((el) => el.id === previewMMedium.value.m_template_id));

  const livePreviewTemplate = computed(() =>
    mCreative.value?.media_type === "image" && previewTemplate.value?.renderer === "html" ? previewTemplate.value : null,
  );

  const livePreviewVariables = computed(() => {
    const attributes = mCreative.value?.m_template_attributes || {};
    const texts = Object.entries(livePreviewTemplate.value?.m_template_attributes || {}).map(([key, spec]) => {
      const value = String(attributes[key] ?? "");
      return [key, value.trim() ? value : String(spec?.default ?? "")];
    });
    return { ...Object.fromEntries(texts), background_image: getPreviewUrl(previewMMedium.value.m_medium_upload_id) || "" };
  });

  const selectedPlacements = computed(() => {
    return ["feed", "story"].filter((placement) => mCreative.value?.placements?.[placement] === true);
  });

  const selectedTemplates = computed(() => creativeTemplates(mCreative.value, mTemplates.value));

  const selectedAssets = computed(() => creativeUploads(mCreative.value, mMediumUploads.value));

  const updatePreviewPlacement = (value) => {
    previewPlacement.value = value;

    if (previewMMedium.value.id) {
      selectMMedium(previewMMedium.value.m_medium_upload_id, previewMMedium.value.m_template_id);
    }
  };

  const isMMediumSelected = (assetId, templateId) => {
    return (
      previewMMedium.value.m_medium_upload_id === assetId &&
      previewMMedium.value.m_template_id === templateId &&
      previewMMedium.value.placement === previewPlacement.value
    );
  };

  const isBuildRunning = (assetId, templateId) => {
    const element = builds.value.find(
      (el) =>
        el.m_creative_token === mCreative.value.token &&
        el.m_medium_upload_id === assetId &&
        el.m_template_id === templateId &&
        el.placement === previewPlacement.value,
    );
    return element?.build || false;
  };

  const selectMMedium = (assetId = 0, templateId = 0) => {
    if (mCreative.value === undefined) return {};
    if (assetId === 0 || templateId === 0) {
      assetId = mCreative.value.m_medium_upload_ids?.[0] || -1;
      templateId = mCreative.value.template_ids?.[0] || -1;
    }
    previewMMedium.value = getMMedium(assetId, templateId);
  };

  const getMMedium = (assetId, templateId) => {
    if ([assetId, templateId].includes(-1)) return {};
    const mMedium = findMMedium(mCreative.value, previewPlacement.value, assetId, templateId, adSetVariables.value);
    if (!mMedium) {
      return {
        id: null,
        url: null,
        m_template_id: templateId,
        m_medium_upload_id: assetId,
        placement: previewPlacement.value,
      };
    }
    return mMedium;
  };

  const getPreviewUrl = (assetId) => {
    return mMediumUploads.value?.find((el) => el.id === assetId)?.url || null;
  };

  const buildMMedia = async () => {
    builds.value = selectedPlacements.value.flatMap((placement) =>
      selectedTemplates.value.flatMap((template) =>
        selectedAssets.value.map((asset) => ({
          m_creative_token: mCreative.value.token,
          m_medium_upload_id: asset.id,
          m_template_id: template.id,
          placement: placement,
          build: true,
        })),
      ),
    );

    try {
      const creativeToken = mCreative.value.token;
      const { response } = await useMyFetch(`/m_creatives/${creativeToken}/m_media/build`)
        .post({ m_creative: mCreative.value })
        .json();

      if (response.value.ok) captureEvent("creative_media_built", { m_creative_token: creativeToken });
    } catch (err) {
      handleError(err);
    }
  };

  const adsChannelHandlers = (cable) => {
    return {
      connected: subscribeToChannel(cable),
      rejected: () => console.log("rejected"),
      received: handleReceivedMessage,
    };
  };

  const subscribeToChannel = (cable) => () => cable.perform({ channel: "AdsChannel", action: "subscribe" });

  const upsertMedium = (mediaList, newMedium) => {
    const oldMedium = mediaList.find((el) => el.id === newMedium.id);
    if (oldMedium) {
      Object.assign(oldMedium, deepCopy(newMedium));
      return false;
    }
    mediaList.push(deepCopy(newMedium));
    return true;
  };

  const handleReceivedMessage = (message) => {
    if (Object.keys(builds.value).length === 0) return;
    if (message?.type !== "MMedium") return;
    const newMedium = message.body;
    const creative = creatives.value.find((c) => c.token === newMedium.m_creative_token);
    const creativeI = initialCreatives.value.find((c) => c.token === newMedium.m_creative_token);
    if (!creative) return;

    const isNewMedium = upsertMedium(creative.m_media, newMedium);
    if (creativeI) upsertMedium(creativeI.m_media, newMedium);

    if (
      isNewMedium &&
      creative.token === mCreative.value.token &&
      isMMediumSelected(newMedium.m_medium_upload_id, newMedium.m_template_id)
    ) {
      selectMMedium(newMedium.m_medium_upload_id, newMedium.m_template_id);
    }

    const element = builds.value.find(
      (el) =>
        el.m_creative_token === newMedium.m_creative_token &&
        el.m_medium_upload_id === newMedium.m_medium_upload_id &&
        el.m_template_id === newMedium.m_template_id &&
        el.placement === newMedium.placement,
    );
    if (!element) return;
    element.build = false;
  };

  return {
    selectedTemplates,
    selectedAssets,
    selectedPlacements,
    previewMMediumUrl,
    livePreviewTemplate,
    livePreviewVariables,
    previewPlacement,
    updatePreviewPlacement,
    isMMediumSelected,
    selectMMedium,
    getMMedium,
    getPreviewUrl,

    isBuildRunning,
    buildMMedia,
    adsChannelHandlers,
  };
});
